
import i18next from 'i18next';
import _ from 'lodash';
import type { Dispatch } from 'redux';

import { createBreakoutRoomsEvent } from '../analytics/AnalyticsEvents';
import { sendAnalytics } from '../analytics/functions';
import {
    conferenceLeft,
    conferenceWillLeave,
    createConference,
    setPassword
} from '../base/conference/actions';
import { CONFERENCE_LEAVE_REASONS } from '../base/conference/constants';
import { getCurrentConference } from '../base/conference/functions';
import { setAudioMuted, setVideoMuted } from '../base/media/actions';
import { MEDIA_TYPE } from '../base/media/constants';
import { setIsSwitchingBreakoutRoom, setIsSwitchingToMainRoom } from '../base/mtApi';
import { fakeParticipantLeft } from '../base/mtApi/actions';
import { FakeParticipant } from '../base/mtApi/constants';
import { getParticipants } from '../base/participants/functions';
import { createDesiredLocalTracks } from '../base/tracks/actions';
import {
    getLocalTracks,
    isLocalTrackMuted,
    isScreenVideoShared
} from '../base/tracks/functions';
import { clearNotifications, showNotification } from '../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../notifications/constants';
import { isAudioOnlySharing } from '../screen-share/functions';

import { SET_PERSISTENT_BREAKOUT_AUTO_ASSIGN, _RESET_BREAKOUT_ROOMS, _UPDATE_ROOM_COUNTER } from './actionTypes';
import { FEATURE_KEY } from './constants';
import {
    getBreakoutRooms,
    getMainRoom,
    getRoomByJid,
    isBreakoutRoomCreationAllowed,
    stopScreenSharingWhileMovingBreakoutRoom } from './functions';
import logger from './logger';


const PASSWORD_RETRY_DELAY_MS = 500;
const PASSWORD_RETRY_MAX_ATTEMPTS = 10;


/**
 * Applies the room passcode once the destination conference is available.
 *
 * @param {Dispatch<any>} dispatch - The redux dispatch function.
 * @param {Function} getState - The redux state getter.
 * @param {string} targetRoomId - The room we are switching to.
 * @param {Object} options - Retry and password options.
 * @returns {void}
 */
function _applyRoomPasswordWhenReady(
        dispatch: Dispatch<any>,
        getState: Function,
        targetRoomId: string,
        options: {
        attemptsLeft?: number,
        password: string
    }) {
    const {
        attemptsLeft = PASSWORD_RETRY_MAX_ATTEMPTS,
        password
    } = options;

    if (!password || attemptsLeft <= 0) {
        if (attemptsLeft <= 0) {
            logger.warn(`Timed out waiting to apply password for room ${targetRoomId}`);
        }

        return;
    }

    const conference = getCurrentConference(getState);
    const conferenceName = conference?.getName?.() || conference?.roomName;

    if (conference && conferenceName === targetRoomId) {
        dispatch(setPassword(conference, conference.join, password));

        return;
    }

    setTimeout(() => _applyRoomPasswordWhenReady(
        dispatch,
        getState,
        targetRoomId,
        {
            attemptsLeft: attemptsLeft - 1,
            password
        }
    ), PASSWORD_RETRY_DELAY_MS);
}


// import BreakoutRoomNamePrompt from './components/web/BreakoutRoomNamePrompt';

declare var APP: Object;


/**
 * Action to create a breakout room.
 *
 * @param {string} name - Name / subject for the breakout room.
 * @returns {Function}
 */
export function createBreakoutRoom(name?: string) {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const state = getState();

        // The button and API can fire again before the server confirms the
        // previous room; the limit must hold for those too.
        if (!isBreakoutRoomCreationAllowed(state)) {
            return;
        }

        let { roomCounter } = state[FEATURE_KEY];
        const subject = name || i18next.t('breakoutRooms.defaultName', { index: ++roomCounter });

        sendAnalytics(createBreakoutRoomsEvent('create'));
        dispatch({
            type: _UPDATE_ROOM_COUNTER,
            roomCounter
        });

        getCurrentConference(state)?.getBreakoutRooms()
            ?.createBreakoutRoom(subject);
    };
}

/**
 * Action to close a room and send participants to the main room.
 *
 * @param {string} roomId - The id of the room to close.
 * @returns {Function}
 */
export function closeBreakoutRoom(roomId: string) {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const rooms = getBreakoutRooms(getState);
        const room = rooms[roomId];
        const mainRoom = getMainRoom(getState);

        sendAnalytics(createBreakoutRoomsEvent('close'));

        if (room && mainRoom) {
            Object.values(room.participants).forEach(p => {
                dispatch(sendParticipantToRoom(p.jid, mainRoom.id));
            });
        }
    };
}

/**
 * Action to rename a breakout room.
 *
 * @param {string} breakoutRoomJid - The jid of the breakout room to rename.
 * @param {string} name - New name / subject for the breakout room.
 * @returns {Function}
 */
export function renameBreakoutRoom(breakoutRoomJid: string, name = '') {
    return (_dispatch: Dispatch<any>, getState: Function) => {
        const trimmedName = name.trim();

        if (trimmedName.length !== 0) {
            sendAnalytics(createBreakoutRoomsEvent('rename'));
            getCurrentConference(getState)?.getBreakoutRooms()
                ?.renameBreakoutRoom(breakoutRoomJid, trimmedName);
        }
    };
}

/**
 * Action to remove a breakout room.
 *
 * @param {string} breakoutRoomJid - The jid of the breakout room to remove.
 * @returns {Function}
 */
export function removeBreakoutRoom(breakoutRoomJid: string): Function {
    return (dispatch: Dispatch<any>, getState: Function) => {
        sendAnalytics(createBreakoutRoomsEvent('remove'));
        const room = getRoomByJid(getState, breakoutRoomJid);

        if (!room) {
            // The room is already gone - a double tap, or another moderator removed it first. That is the
            // outcome this action was asking for, so it is not a failure.
            logger.warn('The room to remove was not found; it is already removed.');

            return;
        }

        if (Object.keys(room.participants).length > 0) {
            dispatch(closeBreakoutRoom(room.id));
        }
        getCurrentConference(getState)?.getBreakoutRooms()
            ?.removeBreakoutRoom(breakoutRoomJid);
    };
}

/**
 * Action to auto-assign the participants to breakout rooms.
 *
 * @returns {Function}
 */
export function autoAssignToBreakoutRooms() {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const rooms = getBreakoutRooms(getState);
        const breakoutRooms = _.filter(rooms, room => !room.isMainRoom);

        if (breakoutRooms && breakoutRooms.length > 0) {
            sendAnalytics(createBreakoutRoomsEvent('auto.assign'));
            const participantIds = getParticipants(getState)
                .filter(p => !p.local)
                .map(p => p.id);
            const length = Math.ceil(participantIds.length / breakoutRooms.length) || participantIds.length;

            _.chunk(_.shuffle(participantIds), length).forEach((group, index) =>
                group.forEach(participantId => {
                    dispatch(sendParticipantToRoom(participantId, breakoutRooms[index % breakoutRooms.length].id));
                })
            );
        }
    };
}

/**
 * Action to set the persistent auto-assign setting.
 *
 * @param {boolean} persistent - Whether auto-assign should be persistent.
 * @returns {{
 *     type: SET_PERSISTENT_BREAKOUT_AUTO_ASSIGN,
 *     persistent: boolean
 * }}
 */
export function setPersistentBreakoutAutoAssign(persistent: boolean) {
    return {
        type: SET_PERSISTENT_BREAKOUT_AUTO_ASSIGN,
        persistent
    };
}


/**
 * Action to send a participant to a room.
 *
 * @param {string} participantId - The participant id.
 * @param {string} roomId - The room id.
 * @returns {Function}
 */
export function sendParticipantToRoom(participantId: string, roomId: string) {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const rooms = getBreakoutRooms(getState);
        const room = rooms[roomId];

        if (!room) {
            logger.warn(`Invalid room: ${roomId}`);

            return;
        }

        // Get the full JID of the participant. We could be getting the endpoint ID or
        // a participant JID. We want to find the connection JID.
        const participantJid = _findParticipantJid(getState, participantId);

        if (!participantJid) {
            logger.warn(`Could not find participant ${participantId}`);

            return;
        }

        getCurrentConference(getState)?.getBreakoutRooms()
            ?.sendParticipantToRoom(participantJid, room.jid);
    };
}

/**
 * Action to move to a room.
 *
 * @param {string} roomId - The room id to move to. If omitted move to the main room.
 * @returns {Function}
 */
export function moveToRoom(roomId?: string) {
    return async (dispatch: Dispatch<any>, getState: Function) => {

        const state = getState();
        const isSharing = isScreenVideoShared(state) || isAudioOnlySharing(state);
        const currentConference = getCurrentConference(getState);

        if (isSharing) {
            const localScreenShare = state['features/base/mtApi']?.localScreenShare;

            if (localScreenShare) {
                dispatch(fakeParticipantLeft(localScreenShare.id, currentConference, {
                    fakeParticipant: FakeParticipant.LocalScreenShare
                }));
            }

            try {
                // Wait for screen sharing to be completely stopped before switching rooms.
                // Without await, the state update may not complete before the room transition,
                // leaving the UI icon showing "sharing" even though the stream is stopped.
                await stopScreenSharingWhileMovingBreakoutRoom(dispatch);
            } catch (e) {
                logger.error('Error stopping screen sharing when moving to breakout room:', e);
            }
        }

        const mainRoomId = getMainRoom(getState)?.id;
        let _roomId: string | undefined | String = roomId || mainRoomId;

        // Determine if we're going to the main room before any transformations
        const goToMainRoom = _roomId?.toString() === mainRoomId;

        // Set the appropriate flag based on whether going to main room or breakout room
        // This is necessary to preserve virtual backgrounds during room transitions
        if (goToMainRoom) {
            dispatch(setIsSwitchingToMainRoom(true));
        } else {
            dispatch(setIsSwitchingBreakoutRoom(true));
        }

        // Check if we got a full JID.
        if (_roomId && _roomId?.indexOf('@') !== -1) {
            const [ id, ...domainParts ] = _roomId.split('@');

            // On mobile we first store the room and the connection is created
            // later, so let's attach the domain to the room String object as
            // a little hack.

            // eslint-disable-next-line no-new-wrappers
            _roomId = new String(id);

            _roomId.domain = domainParts.join('@');


        }

        const roomIdStr = _roomId?.toString();

        const rooms = getBreakoutRooms(getState);
        const targetRoom = rooms[roomIdStr ?? ''];

        if (!targetRoom) {
            logger.warn(`Unknown room: ${targetRoom}`);

            return;
        }

        dispatch({
            type: _RESET_BREAKOUT_ROOMS
        });

        const preregData = getState()['features/base/mtApi']?.pregData ?? {};
        const passcode = preregData?.passcode;

        if (navigator.product === 'ReactNative') {
            const conference = getCurrentConference(getState);
            const { audio, video } = getState()['features/base/media'];

            dispatch(conferenceWillLeave(conference));

            try {
                await conference?.leave(CONFERENCE_LEAVE_REASONS.SWITCH_ROOM);
            } catch (error) {
                logger.warn('MHConference.leave() rejected with:', error);

                dispatch(conferenceLeft(conference));
            }

            dispatch(clearNotifications());
            dispatch(createConference(_roomId));
            dispatch(setAudioMuted(audio.muted));
            dispatch(setVideoMuted(Boolean(video.muted)));
            dispatch(createDesiredLocalTracks());

            if (passcode) {
                _applyRoomPasswordWhenReady(dispatch, getState, roomIdStr ?? '', { password: passcode });
            }
        } else {
            const localTracks = getLocalTracks(getState()['features/base/tracks']);
            const isAudioMuted = isLocalTrackMuted(localTracks, MEDIA_TYPE.AUDIO);
            const isVideoMuted = isLocalTrackMuted(localTracks, MEDIA_TYPE.VIDEO);

            try {
                // all places we fire notifyConferenceLeft we pass the room name from APP.conference
                await APP.conference.leaveRoom(false /* doDisconnect */, CONFERENCE_LEAVE_REASONS.SWITCH_ROOM).then(
                    () => APP.API.notifyConferenceLeft(APP.conference.roomName));
            } catch (error) {
                logger.warn('APP.conference.leaveRoom() rejected with:', error);

                // TODO: revisit why we don't dispatch CONFERENCE_LEFT here.
            }

            APP.conference.joinRoom(_roomId, {
                startWithAudioMuted: isAudioMuted,
                startWithVideoMuted: isVideoMuted
            });

            if (passcode) {
                _applyRoomPasswordWhenReady(dispatch, getState, roomIdStr ?? '', { password: passcode });
            }
        }

        if (goToMainRoom) {
            dispatch(showNotification({
                titleKey: 'breakoutRooms.notifications.joinedTitle',
                descriptionKey: 'breakoutRooms.notifications.joinedMainRoom',
                concatText: true,
                maxLines: 2
            }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));

        } else {
            dispatch(showNotification({
                titleKey: 'breakoutRooms.notifications.joinedTitle',
                descriptionKey: 'breakoutRooms.notifications.joined',
                descriptionArguments: { name: targetRoom.name },
                concatText: true,
                maxLines: 2
            }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        }
    };
}

/**
 * Finds a participant's connection JID given its ID.
 *
 * @param {Function} getState - The redux store state getter.
 * @param {string} participantId - ID of the given participant.
 * @returns {string|undefined} - The participant connection JID if found.
 */
function _findParticipantJid(getState: Function, participantId: string) {
    const conference = getCurrentConference(getState);

    if (!conference) {
        return;
    }

    // Get the full JID of the participant. We could be getting the endpoint ID or
    // a participant JID. We want to find the connection JID.
    let _participantId = participantId;
    let participantJid;

    if (typeof participantId === 'undefined' || typeof participantId !== 'string' || !participantId.includes('@')) {
        const p = conference.getParticipantById(participantId);

        _participantId = p?.getJid(); // This will be the room JID.
    }

    if (_participantId) {
        const rooms = getBreakoutRooms(getState);

        for (const room of Object.values(rooms)) {
            const participants = room.participants || {};
            const p = participants[_participantId]
                || Object.values(participants).find(item => item.jid === _participantId);

            if (p) {
                participantJid = p.jid;
                break;
            }
        }

        if (!participantJid && _participantId.includes('@')) {
            participantJid = _participantId;
        }
    }

    return participantJid;
}


// export const hideRenameDialog =() => hideDialog(BreakoutRoomNamePrompt);
